import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import type { ColDef } from 'ag-grid-community'
import { map, sortBy, toLower } from 'lodash-es'
import AddchartIcon from '@mui/icons-material/Addchart'
import ShowChart from '@mui/icons-material/ShowChart'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'

import {
  ISelectOption,
  LineSegmentGenericData,
  QRADatasetRowEntry
} from '$/types'
import { useAppSelector } from 'app/hooks'
import { DraggableColumn } from 'common/components/DraggableColumn'
import LoadingSpinner, {
  SpinnerWithOverlay
} from 'common/components/LoadingSpinner'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'
import useGetCurrentLine from 'common/hooks/getCurrentLine'

import DataInputGraph from 'features/RiskManagement/Components/Graphs/Implementations/DataInputGraph'
import {
  selectRiskUnit,
  selectSavedFilter
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import {
  ILineTableViewProps,
  QRATableLineViewMode
} from 'features/RiskManagement/Components/Tables'
import CSVExportButton from 'features/RiskManagement/Components/Tables/Toolbar/CSVExportButton'
import GroupColumns from 'features/RiskManagement/Components/Tables/Toolbar/GroupColumns'
import LineViewToggle from 'features/RiskManagement/Components/Tables/Toolbar/LineViewToggle'
import ThreatDropdown from 'features/RiskManagement/Components/Tables/Toolbar/ThreatDropdown'
import {
  floatValueFormatter,
  formatYearColDefs,
  generateOverrideMap,
  overrideValueFormatter
} from 'features/RiskManagement/Components/Tables/utils/formatters'

import tailwind from 'styles/themeTokens'

import { autoSizeAllColumns } from '../../../helpers'
import {
  decimalPrecision,
  formatWithPrecision,
  ONE_SET_RAW,
  THREE_SET_RAW,
  ZERO_SET_RAW
} from '../../Graphs/Implementations/attributeFormatters'
import { quickFilterParser } from '../utils/data'

const QRATableWithToolbarLazy = lazy(
  () => import('../../../../../common/components/QRATableWrapper/QRATableView')
)

type AttributeDataType = number[] | string[] | boolean[]

interface IAtrributeData {
  name: string
  dataSet1: AttributeDataType
  dataSet2: AttributeDataType
}

const LineTableView = ({
  showLoading = false,
  colDefs,
  rowData,
  s1Label,
  lineViewMode,
  units,
  setLineViewMode,
  groupColumns,
  setGroupColumns,
  mutationTrigger,
  exportParams,
  isCompareView
}: ILineTableViewProps) => {
  const [XValues, setXValues] = useState<number[]>([])
  const [columns, setColumns] = useState<ISelectOption[]>([])

  const [selectedAttributes, setSelectedAttributes] = useState<ISelectOption[]>(
    []
  )

  const [attributeData, setAttributeData] = useState<IAtrributeData[]>([])

  const [showCharts, setShowCharts] = useState<boolean>(false)
  const [collapse, setCollapse] = useState<boolean>(true)

  const riskUnit = useAppSelector(selectRiskUnit)

  const selectedLine = useGetCurrentLine()
  const localFilter = useAppSelector(selectSavedFilter)

  useEffect(() => {
    const ignoreColumnFields = [
      'SegmentId',
      'StartMeasure',
      'FromMeasure',
      'Length',
      'LengthMiles',
      'RouteName',
      'ToMeasure',
      'EndMeasure'
    ]

    const ignoreDiffColumnFieldsForCharts = [
      'ConsequenceAreaCode',
      'ClassLocationCode',
      'OHCAFlag'
    ]

    if (isCompareView) {
      ignoreColumnFields.push(...ignoreDiffColumnFieldsForCharts)
    }

    if (colDefs.length > 0) {
      // using a set here to dedupe; can just map if that's not a concern
      const _fieldSet: Set<ISelectOption> = new Set(
        colDefs
          .filter(
            (cd: ColDef) =>
              cd.field != null &&
              !ignoreColumnFields.includes(cd.field as string)
          )
          .map((cd: ColDef) => ({
            label: cd.headerName?.replace(/\(.*?\)/g, '') as string,
            value: cd.field as string
          }))
      )

      setColumns(Array.from(_fieldSet))
    }
  }, [colDefs, isCompareView])

  useEffect(() => {
    if (rowData) {
      const _updateLinearGraphData = () => {
        const tempXValues: number[] = []
        const tempAttributeData: IAtrributeData[] = []

        // * slice to make a new rowData array to not mutate it by sorting
        //To Arrange the records from Line start to end
        // * typing is off here because of reassignments below. should not be picked, but full
        let tempRowData: Pick<
          QRADatasetRowEntry,
          'FromMeasure' | 'ToMeasure'
        >[] = sortBy(rowData, 'ToMeasure')

        // Handles inserting dummy values for gaps in between valid segments
        if (tempRowData?.length > 0 && columns.length > 0) {
          const newTempRowData = []

          for (let i = 0; i < tempRowData.length - 1; ++i) {
            const segment = tempRowData[i]

            newTempRowData.push(segment)

            if (segment.ToMeasure !== tempRowData[i + 1].FromMeasure) {
              const tempObj = {
                FromMeasure: segment.ToMeasure,
                ToMeasure: tempRowData[i + 1].FromMeasure
              }

              newTempRowData.push(tempObj)
            }
          }

          newTempRowData.push(tempRowData[tempRowData.length - 1])
          tempRowData = newTempRowData
        }

        const localFilterFiltered = sortBy(
          localFilter.segments.filter((segs) => segs.name === selectedLine)
        )

        //Handles adding null values for endpoints (only runs if the user selects a line distance filter)
        if (tempRowData.length > 0 && localFilterFiltered.length > 0) {
          if (localFilterFiltered[0].fromMeasure < tempRowData[0].FromMeasure) {
            const tempObj = {
              FromMeasure: localFilterFiltered[0].fromMeasure,
              ToMeasure: tempRowData[0].FromMeasure
            }

            tempRowData.unshift(tempObj)
          }

          // Check and insert the right buffer if there is a gap between the last line segment and the upper local filter
          if (
            localFilterFiltered[localFilterFiltered.length - 1].toMeasure >
            tempRowData[tempRowData.length - 1].ToMeasure
          ) {
            const tempObj = {
              FromMeasure: tempRowData[tempRowData.length - 1].ToMeasure,
              ToMeasure:
                localFilterFiltered[localFilterFiltered.length - 1].toMeasure
            }

            tempRowData.push(tempObj)
            tempRowData.push(tempObj)
          }
        }

        if (tempRowData != null && columns.length > 0) {
          for (let i = 0; i < columns.length; i++) {
            const rawAttrName = String(columns[i].value).trim()

            // ──────────────────────────────────────────────────────────────
            //  1 .  Pull the raw values for Baseline and (optionally) WIS
            // ──────────────────────────────────────────────────────────────
            let baseLineData = map(tempRowData, rawAttrName) // Baseline
            let WISData: (string | number)[] = [] // What-If

            if (exportParams.assessmentId !== undefined) {
              baseLineData = map(tempRowData, `${rawAttrName}_ast1`)
              WISData = map(tempRowData, `${rawAttrName}_ast2`)
            }

            // ──────────────────────────────────────────────────────────────
            //  2 .  Decide which formatter (if any) to apply
            // ──────────────────────────────────────────────────────────────
            let formatter: ((v: number | string) => string) | null = null

            if (THREE_SET_RAW.has(rawAttrName)) {
              formatter = (v) => formatWithPrecision(v, 3, 'NaN')
            } else if (ONE_SET_RAW.has(rawAttrName)) {
              formatter = (v) => formatWithPrecision(v, 1, 'NaN')
            } else if (ZERO_SET_RAW.has(rawAttrName)) {
              formatter = (v) => formatWithPrecision(v, 0, 'NaN')
            }

            if (formatter) {
              baseLineData = baseLineData.map(formatter)
              WISData = WISData.map(formatter)
            }

            // ──────────────────────────────────────────────────────────────
            //  3 .  Push the formatted data into tempAttributeData
            // ──────────────────────────────────────────────────────────────
            tempAttributeData.push({
              name: rawAttrName,
              dataSet1: baseLineData as string[], // now strings when formatted
              dataSet2: WISData as string[]
            })
          }

          for (let i = 0; i < tempRowData?.length; i++) {
            if (i === 0) {
              tempXValues.push(tempRowData[i].FromMeasure)
            }

            if (i < tempRowData?.length - 1) {
              if (tempRowData[i].ToMeasure === tempRowData[i + 1].FromMeasure) {
                tempXValues.push(tempRowData[i].ToMeasure)
              } else {
                tempXValues.push(tempRowData[i].ToMeasure)
                tempXValues.push(tempRowData[i + 1].ToMeasure)
              }
            } else {
              tempXValues.push(tempRowData[i].ToMeasure)
            }
          }

          setXValues(tempXValues)
        }

        setAttributeData(tempAttributeData)
      }

      const _updateDefaultGraphs = () => {
        const tempSelectedAttributes: ISelectOption[] = []

        columns.forEach((i) => {
          if (tempSelectedAttributes.length < 5) {
            tempSelectedAttributes.push({ label: i.label, value: i.value })
          }
        })
        setSelectedAttributes(tempSelectedAttributes)
      }

      _updateLinearGraphData()
      _updateDefaultGraphs()
    }
  }, [columns, rowData, localFilter, selectedLine, exportParams.assessmentId])

  const formatAttributeData = (
    attributeData: AttributeDataType | undefined
  ) => {
    return attributeData?.map((attr) => {
      return typeof attr === 'boolean' ? String(attr) : attr
    })
  }

  const filteredData = selectedAttributes.map((attr) => {
    const selectedAttributeData = attributeData.find(
      (data) => data.name === attr.value
    )

    if (selectedAttributeData) {
      return {
        name: attr.label as string,
        code: attr.value as string,
        dataSet1: formatAttributeData(selectedAttributeData.dataSet1),
        dataSet2: formatAttributeData(selectedAttributeData.dataSet2)
      }
    } else {
      return {
        name: attr.label as string,
        code: attr.value as string,
        dataSet1: undefined,
        dataSet2: undefined
      }
    }
  })

  const setGraphView = (): JSX.Element => {
    return (
      <>
        {showCharts && (
          <div
            className={[
              'relative bg-gray-200 p-3',
              collapse ? 'w-4/12' : 'w-6/12'
            ].join(' ')}
          >
            <OverlayScrollbar scrollStyle='absolute left-3 right-0 top-0 bottom-0 pr-3 overflow-auto'>
              <div className='font-semibold'>
                <div className='flex items-center px-2 py-3'>
                  <ShowChart />
                  <div className='ml-2'>Chart Preview</div>
                  {collapse ? (
                    <UnfoldMoreIcon
                      className='ml-auto rotate-90'
                      onClick={() => {
                        setCollapse(false)
                      }}
                    />
                  ) : (
                    <UnfoldLessIcon
                      className='ml-auto rotate-90'
                      onClick={() => {
                        setCollapse(true)
                      }}
                    />
                  )}
                </div>
                <SelectDropdownWrapper
                  className='mb-2 w-full'
                  mode='tags'
                  options={columns.map((i) => ({
                    label: i.label,
                    value: String(i.value)
                  }))}
                  value={selectedAttributes.map(({ value }) => String(value))}
                  onChange={(_, selections) => {
                    setSelectedAttributes(selections as ISelectOption[])
                  }}
                  placeholder='Select Attributes'
                />
                <DraggableColumn>
                  {filteredData.map((attr, index) => {
                    let yAxisOptions: LineSegmentGenericData[] = []

                    const dataFiltered = attr.dataSet1?.filter(
                      (data) => data != null
                    )

                    if (dataFiltered && typeof dataFiltered[0] !== 'number') {
                      let dataArray = [...dataFiltered]

                      if (attr.dataSet2) {
                        dataArray = dataArray.concat(attr.dataSet2)
                      }

                      const combinedOptions = Array.from(
                        new Set(dataArray)
                      ).filter((data) => data != null)

                      if (
                        combinedOptions.length < 2 &&
                        typeof combinedOptions[0] == 'string'
                      ) {
                        if (toLower(combinedOptions[0]) === 'true') {
                          combinedOptions.push('False')
                        } else if (toLower(combinedOptions[0]) === 'false') {
                          combinedOptions.push('True')
                        }
                      }

                      yAxisOptions = sortBy(combinedOptions)
                    }

                    let yAxisTitle = ''

                    if (
                      attr.dataSet1 &&
                      attr.dataSet1.length > 0 &&
                      typeof attr.dataSet1[0] == 'number'
                    ) {
                      yAxisTitle =
                        units.find((col) => col.field === attr.code)?.unit ?? ''
                    }

                    return (
                      <div key={index}>
                        <DataInputGraph
                          isDraggable
                          showLoading={false}
                          riskUnit={riskUnit}
                          XValues={XValues}
                          s1Values={attr.dataSet1 ?? []}
                          s2Values={attr.dataSet2 ?? []}
                          s1Label={s1Label ?? 'Baseline'}
                          s2Label='What-If Scenario'
                          XLabel='Distance (ft)'
                          YLabel={decodeURIComponent(yAxisTitle)}
                          graphIndexNumber={index}
                          attributeName={attr.name ?? 'Select Attribute'}
                          selectedAttributes={selectedAttributes}
                          lineColor1={
                            attr?.dataSet2?.length
                              ? tailwind.theme.colors.gray[700]
                              : tailwind.theme.colors.lighthouseBlue.DEFAULT
                          }
                          lineColor2={
                            tailwind.theme.colors.lighthouseBlue.DEFAULT
                          }
                          isContinuous
                          yAxisOptions={yAxisOptions}
                        />
                      </div>
                    )
                  })}
                </DraggableColumn>
              </div>
            </OverlayScrollbar>
          </div>
        )}
      </>
    )
  }

  const setLineViewModeWrapper = (viewMode: QRATableLineViewMode) => {
    setLineViewMode(viewMode)

    if (viewMode === QRATableLineViewMode.Output) {
      // REVIEW: Create method that provides the default output table selection
      setGroupColumns([{ id: 0, name: 'Threat', groupTypeColumns: undefined }])
    } else {
      setGroupColumns([])
    }
  }

  let toolbarContent: React.ReactNode = <></>

  if (lineViewMode === QRATableLineViewMode.Output) {
    toolbarContent = (
      <>
        <LineViewToggle
          viewMode={lineViewMode}
          setViewMode={setLineViewModeWrapper}
        />
        <div className='mx-4 rounded-sm'>
          <CSVExportButton
            exportParams={exportParams}
            mutationTrigger={mutationTrigger}
          />
        </div>
        <GroupColumns
          groupColumns={groupColumns}
          setGroupColumns={setGroupColumns}
        />
        <div
          className={[
            'ml-auto mr-2 flex text-gray-700',
            'items-center py-1 pr-3',
            'pl-2 font-medium',
            'leading-none'
          ].join(' ')}
        >
          <div
            className={`${
              showCharts ? 'rounded-sm bg-gray-100 text-lighthouseBlue' : ''
            } cursor-pointer px-2`}
            onClick={() => {
              setShowCharts(!showCharts)
              setCollapse(true)
            }}
          >
            <AddchartIcon className='mr-1' />
            <span> Charts</span>
          </div>
        </div>
      </>
    )
  } else {
    // Input Table Toolbar
    toolbarContent = (
      <>
        <LineViewToggle
          viewMode={lineViewMode}
          setViewMode={setLineViewModeWrapper}
        />
        <div className='mx-4 rounded-sm'>
          <CSVExportButton
            exportParams={exportParams}
            mutationTrigger={mutationTrigger}
          />
        </div>
        <ThreatDropdown
          setGroupColumns={setGroupColumns}
          disabled={showLoading}
        />
        <div
          className={[
            'ml-auto mr-2 flex text-gray-700',
            'items-center py-1 pr-3',
            'pl-2 font-medium',
            'leading-none'
          ].join(' ')}
        >
          <div
            className={`${
              showCharts ? 'rounded-sm bg-gray-100 text-lighthouseBlue' : ''
            } cursor-pointer px-2`}
            onClick={() => {
              setShowCharts(!showCharts)
              setCollapse(true)
            }}
          >
            <AddchartIcon className='mr-1' />
            <span> Charts</span>
          </div>
        </div>
      </>
    )
  }

  const colDefsMemo = useMemo(() => {
    if (!colDefs) {
      // more notes on this in ../utils/formatters:10
      return []
    }

    const yearOverrideMap = generateOverrideMap(
      colDefs,
      'date',
      formatYearColDefs
    )

    return colDefs.map((colDef) => {
      const cd: ColDef = {
        ...overrideValueFormatter(colDef, yearOverrideMap),
        sortable: true
      }

      // This new logic converts all data to 3 decimal precision 0 --> 0.000
      if (
        cd.field === 'FromMeasure' ||
        cd.field === 'ToMeasure' ||
        cd.field === 'Length'
      ) {
        cd.valueFormatter = floatValueFormatter
      } else if (cd.field === 'LengthMiles') {
        cd.valueFormatter = ({ value }) => decimalPrecision(value, 5)
      }

      return cd
    })
  }, [colDefs])

  // TODO: [FE DEBT] update QRATableWithToolbar type not to require rowSelectCB
  return (
    <div className='flex flex-1'>
      <div className='mt-2 flex flex-1 flex-col bg-white p-2'>
        <Suspense fallback={<SpinnerWithOverlay />}>
          <LoadingSpinner isLoading={showLoading}>
            <QRATableWithToolbarLazy
              tableClassName='ag-theme-lighthouse'
              toolbarContent={toolbarContent}
              colDefs={colDefsMemo}
              rowData={rowData ?? []}
              rowSelectCB={() => undefined}
              tableSideContent={setGraphView()}
              quickFilterParser={quickFilterParser}
              onFirstDataRendered={autoSizeAllColumns}
            />
          </LoadingSpinner>
        </Suspense>
      </div>
    </div>
  )
}

export default LineTableView
